// FO-393 (batch-46) — pure 3-way-match-engine (order <-> ontvangst <-> factuur).
//
// Geen DB, geen Fastify, geen node:*, GEEN AI-call (de extractie leverde de
// factuurregels al). De service eromheen leest de order-/ontvangst-/factuurregels
// + toleranties uit de DB, roept deze functie aan en schrijft het resultaat weg.
// Geld = number (euro); aantal = number (stuks/uren). Vergelijkingen op centen om
// float-drift te vermijden. Regel-koppeling is deterministisch: genormaliseerde
// omschrijving (Sorensen-Dice) + aantal/prijs-nabijheid; ambigu of geen kandidaat
// telt bewust als afwijking (`KOPPELING_ONZEKER`), nooit stil gokken.

import { normaliseerOmschrijving } from './inkoop-tekst'
import { diceSimilarity } from './fuzzy-match'

// Koppeling-drempels: minimale tekstscore om te koppelen, en de marge waarbinnen
// twee kandidaten "te dicht bij elkaar" (ambigu) zijn.
const KOPPEL_DREMPEL = 0.4
const AMBIGU_MARGE = 0.1
// Aantal-tolerantie voor het teveel-gefactureerd-oordeel (in stuks).
const AANTAL_EPS = 0.001

export type InkoopMatchRegelOordeelValue =
  | 'MATCH_OK'
  | 'PRIJSAFWIJKING'
  | 'TEVEEL_GEFACTUREERD'
  | 'FACTUUR_ZONDER_ONTVANGST'
  | 'KOPPELING_ONZEKER'

export type InkoopMatchStatusValue = 'MATCH_OK' | 'MATCH_AFWIJKING'

export interface MatchOrderRegel {
  id: string
  omschrijving: string
  besteldAantal: number
  /** Prijs per stuk (order). */
  prijs: number
}

export interface MatchFactuurRegel {
  id: string
  omschrijving: string
  aantal: number
  prijsPerStuk: number
  /** Handmatige koppeling (blijft leidend bij herdraai); null = automatisch koppelen. */
  handmatigOrderRegelId?: string | null
}

export interface MatchToleranties {
  /** Relatieve tolerantie in procenten (2 = 2%). */
  pct: number
  /** Absolute tolerantie in euro (5 = EUR 5,00). */
  absoluut: number
}

export interface MatchInput {
  orderRegels: MatchOrderRegel[]
  /** Ontvangen aantal per orderRegelId (cumulatief; legacy-fallback door de service). */
  ontvangenPerOrderRegel: Record<string, number>
  /** Reeds gefactureerd aantal per orderRegelId uit ANDERE niet-geannuleerde facturen (credits negatief). */
  reedsGefactureerdPerOrderRegel: Record<string, number>
  factuurRegels: MatchFactuurRegel[]
  toleranties: MatchToleranties
  /** Heeft de order ueberhaupt een (deel)ontvangst? False -> FACTUUR_ZONDER_ONTVANGST. */
  orderHeeftOntvangst: boolean
}

export interface MatchRegelResultaat {
  factuurRegelId: string
  orderRegelId: string | null
  handmatigGekoppeld: boolean
  oordeel: InkoopMatchRegelOordeelValue
  besteldAantal: number | null
  ontvangenAantal: number | null
  /** Cumulatief gefactureerd op de gekoppelde orderregel (netto, incl. deze factuur). */
  gefactureerdAantal: number
  orderPrijs: number | null
  factuurPrijs: number
  toelichting: string | null
}

export interface MatchResultaat {
  status: InkoopMatchStatusValue
  /** Unieke set van niet-OK oordelen op de factuur. */
  afwijkingstypes: InkoopMatchRegelOordeelValue[]
  regels: MatchRegelResultaat[]
}

function centen(n: number): number {
  return Math.round(n * 100)
}

/** Nabijheid 0..1: 1 = gelijk, daalt met de relatieve afstand. */
function nabijheid(a: number, b: number): number {
  const noemer = Math.max(1, Math.abs(a), Math.abs(b))
  return Math.max(0, 1 - Math.abs(a - b) / noemer)
}

export interface MatchKoppeling {
  factuurRegelId: string
  orderRegelId: string | null
  handmatig: boolean
}

/**
 * Koppel elke factuurregel aan hooguit één orderregel. Handmatige koppeling wint;
 * anders deterministisch op genormaliseerde omschrijving (Dice) met aantal/prijs
 * als tie-breaker. Geen kandidaat boven de drempel, of twee ononderscheidbare
 * kandidaten -> null (KOPPELING_ONZEKER).
 */
export function koppelInkoopRegels(
  orderRegels: MatchOrderRegel[],
  factuurRegels: MatchFactuurRegel[],
): MatchKoppeling[] {
  const genormOrder = orderRegels.map((o) => ({ o, norm: normaliseerOmschrijving(o.omschrijving) }))
  return factuurRegels.map((fr) => {
    if (fr.handmatigOrderRegelId) {
      const bestaat = orderRegels.some((o) => o.id === fr.handmatigOrderRegelId)
      return {
        factuurRegelId: fr.id,
        orderRegelId: bestaat ? fr.handmatigOrderRegelId : null,
        handmatig: bestaat,
      }
    }
    if (genormOrder.length === 0) {
      return { factuurRegelId: fr.id, orderRegelId: null, handmatig: false }
    }
    const normFactuur = normaliseerOmschrijving(fr.omschrijving)
    const scores = genormOrder
      .map(({ o, norm }) => ({
        id: o.id,
        tekst: diceSimilarity(normFactuur, norm),
        aantal: nabijheid(fr.aantal, o.besteldAantal),
        prijs: nabijheid(fr.prijsPerStuk, o.prijs),
      }))
      // Sorteer op tekst, dan aantal-nabijheid, dan prijs-nabijheid (deterministisch).
      .sort((a, b) => b.tekst - a.tekst || b.aantal - a.aantal || b.prijs - a.prijs)

    const beste = scores[0]
    if (!beste || beste.tekst < KOPPEL_DREMPEL) {
      return { factuurRegelId: fr.id, orderRegelId: null, handmatig: false }
    }
    // Kandidaten binnen de ambigu-marge van de beste tekstscore.
    const nabij = scores.filter((s) => beste.tekst - s.tekst < AMBIGU_MARGE)
    if (nabij.length > 1) {
      // Probeer te ontknopen op aantal + prijs; alleen als de beste daar duidelijk
      // uitspringt koppelen we, anders onzeker (nooit gokken).
      const opAantalPrijs = [...nabij].sort(
        (a, b) => b.aantal + b.prijs - (a.aantal + a.prijs),
      )
      const top = opAantalPrijs[0]
      const runner = opAantalPrijs[1]
      const duidelijk = top && runner && top.aantal + top.prijs - (runner.aantal + runner.prijs) >= 0.2
      if (!duidelijk) {
        return { factuurRegelId: fr.id, orderRegelId: null, handmatig: false }
      }
      return { factuurRegelId: fr.id, orderRegelId: top.id, handmatig: false }
    }
    return { factuurRegelId: fr.id, orderRegelId: beste.id, handmatig: false }
  })
}

function binnenTolerantie(verwacht: number, werkelijk: number, tol: MatchToleranties): boolean {
  const grens = Math.max(Math.abs(centen(verwacht)) * (tol.pct / 100), centen(tol.absoluut))
  return Math.abs(centen(werkelijk) - centen(verwacht)) <= grens
}

/**
 * Draait de 3-way-match voor EEN factuur. De service levert de reeds-gefactureerde
 * aantallen uit de andere niet-geannuleerde facturen aan; hier wordt het netto
 * cumulatief (incl. deze factuur) getoetst tegen het ontvangen aantal.
 */
export function draaiInkoopMatch(input: MatchInput): MatchResultaat {
  const orderMap = new Map(input.orderRegels.map((o) => [o.id, o]))
  const koppelingen = koppelInkoopRegels(input.orderRegels, input.factuurRegels)
  const factuurMap = new Map(input.factuurRegels.map((f) => [f.id, f]))

  // Netto cumulatief per orderregel = reeds (andere facturen) + deze factuur.
  const ditPerOrderRegel = new Map<string, number>()
  for (const k of koppelingen) {
    if (!k.orderRegelId) continue
    const fr = factuurMap.get(k.factuurRegelId)
    if (!fr) continue
    ditPerOrderRegel.set(k.orderRegelId, (ditPerOrderRegel.get(k.orderRegelId) ?? 0) + fr.aantal)
  }

  const afwijkingen = new Set<InkoopMatchRegelOordeelValue>()
  const regels: MatchRegelResultaat[] = koppelingen.map((k) => {
    const fr = factuurMap.get(k.factuurRegelId)!
    if (!k.orderRegelId) {
      afwijkingen.add('KOPPELING_ONZEKER')
      return {
        factuurRegelId: fr.id,
        orderRegelId: null,
        handmatigGekoppeld: false,
        oordeel: 'KOPPELING_ONZEKER',
        besteldAantal: null,
        ontvangenAantal: null,
        gefactureerdAantal: fr.aantal,
        orderPrijs: null,
        factuurPrijs: fr.prijsPerStuk,
        toelichting: 'Geen eenduidige orderregel; koppel handmatig.',
      }
    }
    const o = orderMap.get(k.orderRegelId)!
    const ontvangen = input.ontvangenPerOrderRegel[o.id] ?? 0
    const reeds = input.reedsGefactureerdPerOrderRegel[o.id] ?? 0
    const gefactureerd = reeds + (ditPerOrderRegel.get(o.id) ?? 0)

    let oordeel: InkoopMatchRegelOordeelValue = 'MATCH_OK'
    let toelichting: string | null = null
    if (!input.orderHeeftOntvangst) {
      oordeel = 'FACTUUR_ZONDER_ONTVANGST'
      toelichting = 'Er is nog geen levering op deze order geregistreerd.'
    } else if (gefactureerd > ontvangen + AANTAL_EPS) {
      oordeel = 'TEVEEL_GEFACTUREERD'
      toelichting = `Gefactureerd (${gefactureerd}) is meer dan ontvangen (${ontvangen}).`
    } else if (!binnenTolerantie(o.prijs * fr.aantal, fr.prijsPerStuk * fr.aantal, input.toleranties)) {
      oordeel = 'PRIJSAFWIJKING'
      toelichting = `Factuurprijs (${fr.prijsPerStuk}) wijkt af van orderprijs (${o.prijs}).`
    }
    if (oordeel !== 'MATCH_OK') afwijkingen.add(oordeel)

    return {
      factuurRegelId: fr.id,
      orderRegelId: o.id,
      handmatigGekoppeld: k.handmatig,
      oordeel,
      besteldAantal: o.besteldAantal,
      ontvangenAantal: ontvangen,
      gefactureerdAantal: gefactureerd,
      orderPrijs: o.prijs,
      factuurPrijs: fr.prijsPerStuk,
      toelichting,
    }
  })

  return {
    status: afwijkingen.size > 0 ? 'MATCH_AFWIJKING' : 'MATCH_OK',
    afwijkingstypes: [...afwijkingen],
    regels,
  }
}

export const INKOOP_MATCH_REGEL_OORDEEL_LABELS: Record<InkoopMatchRegelOordeelValue, string> = {
  MATCH_OK: 'Match OK',
  PRIJSAFWIJKING: 'Prijsafwijking',
  TEVEEL_GEFACTUREERD: 'Teveel gefactureerd',
  FACTUUR_ZONDER_ONTVANGST: 'Factuur zonder ontvangst',
  KOPPELING_ONZEKER: 'Koppeling onzeker',
}
